/**
 * manager-collection.js
 * Gerencia páginas de coleção: filtros AJAX, infinite scroll e grid switching.
 * Disponível como window.CollectionManager.
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   Grade:
 *     data-collection-grid           → container da grade (id="CollectionGrid")
 *     data-infinite-scroll="true"    → ativa infinite scroll
 *     data-product-count             → exibe contagem atual de produtos carregados
 *
 *   Grid switching (desktop):
 *     data-grid-switcher             → wrapper dos botões
 *     data-grid-toggle               → alterna 3/4 colunas
 *     .grid-icon-3 / .grid-icon-4   → ícones dos botões (hidden conforme estado)
 *
 *   Grid switching (mobile):
 *     data-grid-switcher-mobile      → wrapper dos botões
 *     data-grid-toggle-mobile        → alterna 1/2 colunas
 *     .grid-icon-mobile-1 / .grid-icon-mobile-2
 *
 *   Drawer de filtros (usa DrawerManager — data-drawer="filters"):
 *     data-filter-remove             → remove tag de filtro ativo (lê href)
 *     data-filter-clear-all          → remove todos os filtros (lê href)
 *     data-filter-price-submit       → aplica filtro de preço
 *     data-filter-count              → exibe contagem de resultados
 *
 *   Paginação / infinite scroll:
 *     data-collection-pagination     → container da paginação
 *     data-infinite-scroll-loading   → indicador de loading (hidden por padrão)
 *
 * ─── Estrutura esperada do filtro ─────────────────────────────────────────
 *
 *   <form id="CollectionFiltersForm">
 *     <!-- inputs de filtro (checkboxes, selects) -->
 *     <!-- .filter-price-input: ignorado no change, aguarda data-filter-price-submit -->
 *   </form>
 *
 *   <!-- Acordeões de filtro: usar <details>/<summary> nativo -->
 *   <details>
 *     <summary>Tamanho</summary>
 *     <div><!-- opções --></div>
 *   </details>
 *
 * ─── Persistência do grid ─────────────────────────────────────────────────
 *
 *   localStorage:
 *     'wc-grid-cols'        → '3' | '4'   (desktop, default '4')
 *     'wc-grid-cols-mobile' → '1' | '2'   (mobile,  default '2')
 */

class CollectionManager {
  constructor() {
    this._grid       = document.querySelector('[data-collection-grid]');
    if (!this._grid) return;

    this._pagination = document.querySelector('[data-collection-pagination]');
    this._isLoading  = false;
    this._firstScroll = false;

    this._onScroll       = this._onScroll.bind(this);
    this._onFilterClick  = this._onFilterClick.bind(this);

    this._init();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    this._initGridSwitcher();
    this._initFilters();

    if (this._grid.dataset.infiniteScroll === 'true') {
      this._initInfiniteScroll();
    }

    this._updateCount();
  }

  // ─── Grid Switching ───────────────────────────────────────────────────────

  _initGridSwitcher() {
    const toggleDesktop = document.querySelector('[data-grid-toggle]');
    const toggleMobile  = document.querySelector('[data-grid-toggle-mobile]');

    if (toggleDesktop) {
      const saved = localStorage.getItem('wc-grid-cols') || '4';
      this._applyGrid(saved, false);
      toggleDesktop.addEventListener('click', () => {
        const current = localStorage.getItem('wc-grid-cols') || '4';
        this._applyGrid(current === '4' ? '3' : '4');
      });
    }

    if (toggleMobile) {
      const saved = localStorage.getItem('wc-grid-cols-mobile') || '2';
      this._applyGridMobile(saved, false);
      toggleMobile.addEventListener('click', () => {
        const current = localStorage.getItem('wc-grid-cols-mobile') || '2';
        this._applyGridMobile(current === '2' ? '1' : '2');
      });
    }
  }

  _applyGrid(cols, save = true) {
    const prev = cols === '4' ? '3' : '4';
    this._grid.classList.remove(`md:grid-cols-${prev}`);
    this._grid.classList.add(`md:grid-cols-${cols}`);
    if (save) localStorage.setItem('wc-grid-cols', cols);
    document.querySelector('.grid-icon-3')?.classList.toggle('hidden', cols === '3');
    document.querySelector('.grid-icon-4')?.classList.toggle('hidden', cols === '4');
  }

  _applyGridMobile(cols, save = true) {
    const prev = cols === '2' ? '1' : '2';
    this._grid.classList.remove(`grid-cols-${prev}`);
    this._grid.classList.add(`grid-cols-${cols}`);
    if (save) localStorage.setItem('wc-grid-cols-mobile', cols);
    document.querySelector('.grid-icon-mobile-1')?.classList.toggle('hidden', cols === '1');
    document.querySelector('.grid-icon-mobile-2')?.classList.toggle('hidden', cols === '2');
  }

  // ─── Filtros AJAX ─────────────────────────────────────────────────────────

  _initFilters() {
    const form = document.querySelector('#CollectionFiltersForm');
    if (form) {
      form.addEventListener('change', e => {
        if (e.target.classList.contains('filter-price-input')) return;
        this._applyFilters();
      });
    }

    document.addEventListener('click', this._onFilterClick);
  }

  _onFilterClick(e) {
    const removeBtn = e.target.closest('[data-filter-remove]');
    if (removeBtn) { e.preventDefault(); this._applyFilters(removeBtn.href); return; }

    const clearAll = e.target.closest('[data-filter-clear-all]');
    if (clearAll) { e.preventDefault(); this._applyFilters(clearAll.href); return; }

    const priceSubmit = e.target.closest('[data-filter-price-submit]');
    if (priceSubmit) { e.preventDefault(); this._applyFilters(); return; }
  }

  async _applyFilters(customUrl) {
    const form = document.querySelector('#CollectionFiltersForm');
    const url  = customUrl || `${window.location.pathname}?${new URLSearchParams(new FormData(form)).toString()}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text = await res.text();
      const doc  = new DOMParser().parseFromString(text, 'text/html');

      this._replaceInner('#CollectionFiltersForm',                      doc);
      this._replaceInner('[data-filter-count]',                         doc);
      this._replaceInner('[data-collection-grid]',                      doc);
      this._replaceInner('[data-collection-pagination]',                doc);
      this._replaceInner('.filter-drawer__active-facets-wrapper',       doc);

      history.pushState({ path: url }, '', url);
      this._updateCount();
    } catch (err) {
      console.error('[CollectionManager] Filter failed:', err);
    }
  }

  _replaceInner(selector, doc) {
    const newEl  = doc.querySelector(selector);
    const currEl = document.querySelector(selector);
    if (newEl && currEl) currEl.innerHTML = newEl.innerHTML;
  }

  // ─── Infinite Scroll ──────────────────────────────────────────────────────

  _initInfiniteScroll() {
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  _onScroll() {
    // Ignora o primeiro evento de scroll (evita trigger imediato ao carregar)
    if (!this._firstScroll) { this._firstScroll = true; return; }
    if (this._isLoading) return;

    const threshold = document.body.scrollHeight - window.innerHeight - 200;
    if (window.scrollY >= threshold) this._loadNextPage();
  }

  async _loadNextPage() {
    const nextLink = this._pagination?.querySelector('.next a, a.next');
    if (!nextLink) return;

    this._isLoading = true;
    this._setInfiniteScrollLoading(true);

    try {
      const res = await fetch(nextLink.href);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const text   = await res.text();
      const doc    = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = doc.querySelector('[data-collection-grid]');

      if (newGrid) {
        [...newGrid.children].forEach(child => this._grid.appendChild(child));
      }

      const newPagination = doc.querySelector('[data-collection-pagination]');
      if (this._pagination && newPagination) {
        this._pagination.innerHTML = newPagination.innerHTML;
      }

      this._updateCount();
    } catch (err) {
      console.error('[CollectionManager] Infinite scroll failed:', err);
    } finally {
      this._isLoading = false;
      this._setInfiniteScrollLoading(false);
    }
  }

  _setInfiniteScrollLoading(show) {
    const el = document.querySelector('[data-infinite-scroll-loading]');
    if (!el) return;
    show ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  // ─── Contador ─────────────────────────────────────────────────────────────

  _updateCount() {
    const count   = this._grid?.querySelectorAll('.productCard').length;
    const display = document.querySelector('[data-product-count]');
    if (display && count !== undefined) display.textContent = count;
  }

  destroy() {
    window.removeEventListener('scroll', this._onScroll);
    document.removeEventListener('click', this._onFilterClick);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CollectionManager = new CollectionManager();
});
